import { FC, useEffect, useState } from "react"
import { trackAssistantEvent } from "../../utils/assistant-analytics"
import { Link } from "react-router-dom"
import { ArrowUpRight, Sparkles, X } from "lucide-react"
import { useAppDispatch, useAppSelector } from "../../hooks"
import {
  selectStaffAiDrawerOpen,
  setStaffAiDrawerOpen
} from "../../../redux/slices/app"
import StaffAiChat from "./index"

/**
 * Global slideout for the staff AI assistant. Deliberately NOT built on the
 * Headless UI SidePanel: that unmounts its children on close, which would
 * drop an in-progress conversation/stream. This drawer stays mounted and
 * slides off-canvas, so chat state survives open/close.
 */
const StaffAiDrawer: FC = () => {
  const dispatch = useAppDispatch()
  const open = useAppSelector(selectStaffAiDrawerOpen)
  const close = () => dispatch(setStaffAiDrawerOpen(false))
  // Mount the chat lazily on first open (avoids token mint + history fetch
  // on every page load), then keep it mounted so state survives close.
  const [hasOpened, setHasOpened] = useState(false)
  useEffect(() => {
    if (open) {
      setHasOpened(true)
      trackAssistantEvent("assistant_opened", { surface: "drawer" })
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") dispatch(setStaffAiDrawerOpen(false))
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [open, dispatch])

  return (
    <>
      <div
        aria-hidden="true"
        onClick={close}
        className={`fixed inset-0 z-40 bg-gray-900/40 transition-opacity duration-300 ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />
      <aside
        aria-hidden={!open}
        className={`fixed inset-y-0 right-0 z-50 flex w-screen max-w-3xl transform flex-col bg-white shadow-xl transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-gray-200 px-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-50 text-violet-900 ring-1 ring-violet-100">
            <Sparkles size={16} />
          </div>
          <p className="font-medium text-neutral-800">AI Assistant</p>
          <Link
            to="/staff-ai"
            onClick={close}
            title="Open full page"
            className="ml-auto inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-teal-200 hover:bg-teal-50 hover:text-teal-700"
          >
            Full page
            <ArrowUpRight size={12} />
          </Link>
          <button
            type="button"
            onClick={close}
            title="Close"
            className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
          >
            <X size={18} />
          </button>
        </div>
        <div className="min-h-0 flex-1">
          {hasOpened && <StaffAiChat variant="drawer" />}
        </div>
      </aside>
    </>
  )
}

export default StaffAiDrawer
